import { InfiniteData } from "@tanstack/react-query";

import { InAppMessageSender } from "../constants/enums";
import {
  InAppConversationMessagesResponse,
  InAppMessage
} from "../support.types";

export const OPTIMISTIC_ID_PREFIX = "optimistic-";

export const buildOptimisticId = (seed: string | number = Date.now()) =>
  `${OPTIMISTIC_ID_PREFIX}${seed}-${Math.random().toString(36).slice(2)}`;

const isOptimistic = (id: string) => id.startsWith(OPTIMISTIC_ID_PREFIX);

type MessagesInfiniteData = InfiniteData<InAppConversationMessagesResponse>;

const bumpItemCount = (
  meta: InAppConversationMessagesResponse["meta"],
  delta: number
): InAppConversationMessagesResponse["meta"] => {
  const itemCount = Math.max(meta.itemCount + delta, 0);
  const pageCount = meta.take > 0 ? Math.ceil(itemCount / meta.take) : 0;
  return {
    ...meta,
    itemCount,
    pageCount,
    hasNextPage: meta.page < pageCount
  };
};

export const insertOrReplaceMessage = (
  current: MessagesInfiniteData | undefined,
  message: InAppMessage
): MessagesInfiniteData | undefined => {
  if (!current || current.pages.length === 0) return current;

  for (const page of current.pages) {
    if (page.data.some((m) => m.id === message.id)) {
      return current;
    }
  }

  const twinPageIndex = current.pages.findIndex((page) =>
    page.data.some(
      (m) =>
        isOptimistic(m.id) &&
        m.sender === InAppMessageSender.Admin &&
        message.sender === InAppMessageSender.Admin &&
        m.text === message.text
    )
  );

  if (twinPageIndex !== -1) {
    const pages = current.pages.map((page, idx) => {
      if (idx !== twinPageIndex) return page;
      return {
        ...page,
        data: page.data.map((m) =>
          isOptimistic(m.id) &&
          m.sender === InAppMessageSender.Admin &&
          message.sender === InAppMessageSender.Admin &&
          m.text === message.text
            ? message
            : m
        )
      };
    });
    return { ...current, pages };
  }

  const [firstPage, ...restPages] = current.pages;
  const updatedFirstPage: InAppConversationMessagesResponse = {
    ...firstPage,
    data: [message, ...firstPage.data],
    meta: bumpItemCount(firstPage.meta, 1)
  };
  const restWithCount = restPages.map((page) => ({
    ...page,
    meta: bumpItemCount(page.meta, 1)
  }));

  return { ...current, pages: [updatedFirstPage, ...restWithCount] };
};

export const removeMessageById = (
  current: MessagesInfiniteData | undefined,
  messageId: string
): MessagesInfiniteData | undefined => {
  if (!current || current.pages.length === 0) return current;

  let removed = false;
  const pages = current.pages.map((page) => {
    const filtered = page.data.filter((m) => m.id !== messageId);
    if (filtered.length === page.data.length) return page;
    removed = true;
    return { ...page, data: filtered };
  });

  if (!removed) return current;

  return {
    ...current,
    pages: pages.map((page) => ({ ...page, meta: bumpItemCount(page.meta, -1) }))
  };
};
